import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, fmtDate } from "@/components/PageHeader";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Download, FileText, Clock, StickyNote, UserCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function RecordDetail({ config, record, onClose, onChanged }) {
  const { hasPerm } = useAuth();
  const canManage = hasPerm(`${config.module}:manage`);
  const canAssign = hasPerm("users:view");
  const [rec, setRec] = useState(record);
  const [timeline, setTimeline] = useState([]);
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    if (!record) return;
    setRec(record);
    const [tl, dc] = await Promise.all([
      api.get(`${config.endpoint}/${record.id}/timeline`).catch(() => ({ data: [] })),
      api.get(`/documents/by-entity?entity_type=${config.entityType}&entity_id=${record.id}`).catch(() => ({ data: [] })),
    ]);
    setTimeline(tl.data); setDocs(dc.data);
    if (canAssign) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [record, config, canAssign]);
  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    setBusy(true);
    try {
      const { data } = await api.put(`${config.endpoint}/${rec.id}`, fields);
      setRec(data); toast.success("Updated"); onChanged && onChanged(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`${config.endpoint}/${rec.id}/notes`, { text: note }); setNote(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", config.entityType); fd.append("entity_id", rec.id);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded"); load(); }
    catch (err) { toast.error(apiError(err)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/download/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const assigneeName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";

  if (!rec) return null;
  return (
    <Sheet open={!!record} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="record-detail">
        <SheetHeader className="p-6 pb-4 border-b border-border">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge value={rec.status} />
            {rec.reference_no && <span className="text-xs text-primary font-semibold">{rec.reference_no}</span>}
          </div>
          <SheetTitle className="text-2xl">{rec[config.labelKey]}</SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="overview" className="p-6">
          <TabsList className="mb-5">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {config.detailFields.map((f) => (
                <div key={f.key}><p className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</p><p className="mt-0.5 font-medium">{rec[f.key] || "—"}</p></div>
              ))}
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Assigned</p><p className="mt-0.5 font-medium">{assigneeName(rec.assigned_to)}</p></div>
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Created</p><p className="mt-0.5 font-medium">{fmtDate(rec.created_at)}</p></div>
            </div>
            {rec.message && <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Message</p><p className="mt-1 text-sm leading-relaxed">{rec.message}</p></div>}

            {canManage && (
              <div className="border-t border-border pt-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Status</Label>
                    <Select value={rec.status} onValueChange={(v) => patch({ status: v })}>
                      <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{config.statuses.map((st) => <SelectItem key={st} value={st}>{st.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {canAssign && (
                    <div className="space-y-1.5"><Label>Assign To</Label>
                      <Select value={rec.assigned_to || ""} onValueChange={(v) => patch({ assigned_to: v })}>
                        <SelectTrigger data-testid="detail-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {config.module === "crm" && (
                    <div className="space-y-1.5"><Label>Follow-up Date</Label>
                      <Input type="date" value={(rec.followup_date || "").slice(0, 10)} onChange={(e) => patch({ followup_date: e.target.value })} data-testid="detail-followup" /></div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="timeline">
            {canManage && (
              <div className="flex gap-2 mb-4">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={2} data-testid="note-input" />
                <Button onClick={addNote} data-testid="add-note-btn">Add</Button>
              </div>
            )}
            <div className="space-y-3">
              {timeline.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No activity yet.</p>}
              {timeline.map((e) => (
                <div key={`${e.type}-${e.at}-${e.label}`} className="flex gap-3" data-testid="timeline-event">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    {e.type === "note" ? <StickyNote className="w-4 h-4 text-gold" /> : e.type === "created" ? <UserCheck className="w-4 h-4 text-success" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 pb-3 border-b border-border">
                    <p className="text-sm">{e.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{e.by ? `${e.by} · ` : ""}{fmtDate(e.at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="documents">
            {canManage && (
              <div className="mb-4">
                <input ref={fileRef} type="file" className="hidden" onChange={upload} accept="image/*,application/pdf,.doc,.docx" data-testid="doc-file-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="doc-upload-btn"><Upload className="w-4 h-4 mr-1.5" /> Upload Document</Button>
              </div>
            )}
            <div className="space-y-2">
              {docs.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No documents.</p>}
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid="doc-item">
                  <FileText className="w-5 h-5 text-primary shrink-0" />
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{d.originalFilename}</p><p className="text-xs text-muted-foreground">{d.source} · {fmtDate(d.created_at)}</p></div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(d)} data-testid="doc-download"><Download className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
